import * as THREE from 'three';

export const IRIDESCENT_WAVE_SCENE_ID = 'iridescent_wave';

export const sceneMeta = {
  id: IRIDESCENT_WAVE_SCENE_ID,
  label: 'Iridescent wave',
  defaultBindings: {},
  paramSpecs: {
    zoom: { min: 1.5, max: 24, label: 'Zoom' },
    segmentBias: { min: 0, max: 1, label: 'Segment bias' },
  },
  defaultParams: {
    zoom: 5,
    segmentBias: 0.55,
  },
  bindingTargets: [
    { key: 'density', label: 'Wave density', min: 0, max: 1 },
    { key: 'speed', label: 'Motion speed', min: 0, max: 1 },
    { key: 'light', label: 'Light', min: 0, max: 1 },
    { key: 'iridescence', label: 'Iridescence', min: 0, max: 1 },
  ],
};

const VERT = /* glsl */ `
uniform float uTime;
uniform float uFrequency;
uniform float uSpeed;
uniform float uAmplitude;

varying vec3 vNormal;
varying vec3 vViewPosition;
varying vec2 vUv;

float heightAt(vec2 p, float t) {
  float w1 = uFrequency * (p.x * 0.85 + p.y * 0.45);
  float w2 = uFrequency * 1.73 * (p.x * 0.4 - p.y * 0.9);
  float h = sin(w1 + t) * 0.55 + sin(w2 - t * 1.07) * 0.32;
  h += sin(uFrequency * 0.35 * (p.x + p.y) + t * 0.6) * 0.18;
  return h * uAmplitude;
}

void main() {
  vUv = uv;
  float t = uTime * uSpeed;
  vec3 pos = position;
  float h = heightAt(pos.xy, t);
  float e = 0.015;
  float hx = heightAt(pos.xy + vec2(e, 0.0), t) - h;
  float hy = heightAt(pos.xy + vec2(0.0, e), t) - h;
  vec3 nObj = normalize(vec3(-hx / e, -hy / e, 1.0));
  pos.z += h;

  vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
  vViewPosition = -mvPosition.xyz;
  vNormal = normalize(normalMatrix * nObj);
  gl_Position = projectionMatrix * mvPosition;
}
`;

const FRAG = /* glsl */ `
uniform float uTime;
uniform vec3 uLightDirView;
uniform float uAmbient;
uniform float uDiffuse;
uniform float uSpecular;
uniform float uIridescence;
uniform float uIridHueSpread;

varying vec3 vNormal;
varying vec3 vViewPosition;
varying vec2 vUv;

vec3 hsv2rgb(vec3 c) {
  vec3 rgb = clamp(abs(mod(c.x * 6.0 + vec3(0.0, 4.0, 2.0), 6.0) - 3.0) - 1.0, 0.0, 1.0);
  return c.z * mix(vec3(1.0), rgb, c.y);
}

void main() {
  vec3 N = normalize(vNormal);
  vec3 V = normalize(vViewPosition);
  vec3 L = normalize(uLightDirView);
  float NdotV = clamp(abs(dot(N, V)), 0.0, 1.0);
  float NdotL = max(dot(N, L), 0.0);

  float hue = fract(0.58 + NdotV * uIridHueSpread + vUv.x * 0.08 + vUv.y * 0.06 + uTime * 0.03);
  vec3 irid = hsv2rgb(vec3(hue, 0.62, 1.0));
  vec3 base = vec3(0.04, 0.06, 0.12);

  vec3 diff = vec3(0.75, 0.82, 0.95) * (uAmbient + NdotL * uDiffuse);
  vec3 H = normalize(L + V);
  float spec = pow(max(dot(N, H), 0.0), 48.0) * uSpecular;

  vec3 albedo = mix(base, irid, uIridescence);
  vec3 col = albedo * diff + spec * vec3(1.0, 0.95, 0.9);
  gl_FragColor = vec4(col, 1.0);
}
`;

function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Tessellated plane with procedural wave displacement and iridescent fragment shading.
 */
export class IridescentWaveScene {
  constructor() {
    this.threeScene = new THREE.Scene();
    this._mesh = null;
    this._material = null;
    this._camera = null;
    this._worldLightDir = new THREE.Vector3(0.45, 0.75, 0.48).normalize();
    this._lightView = new THREE.Vector3();
    this._time = 0;
    this._params = { ...sceneMeta.defaultParams };
    this._segmentsX = 96;
    this._segmentsY = 96;
    this._inited = false;
  }

  getMidiTargets() {
    return sceneMeta.bindingTargets;
  }

  getSceneLabel() {
    return sceneMeta.label;
  }

  /**
   * @param {{ segmentBias?: number }} params
   */
  applyParams(params = {}) {
    this._params = { ...sceneMeta.defaultParams, ...params };
    this._syncCameraZoom();
    const bias = clamp01(Number(this._params.segmentBias) || 0.55);
    const sx = Math.round(lerp(48, 200, bias));
    const sy = Math.round(lerp(48, 200, bias));
    if (sx !== this._segmentsX || sy !== this._segmentsY) {
      this._segmentsX = sx;
      this._segmentsY = sy;
      this._rebuildMesh();
    }
  }

  /**
   * @param {{ getSize?: () => { w: number, h: number }, getCamera?: () => THREE.Camera }} [ctx]
   */
  _syncCameraZoom() {
    if (!this._camera) return;
    const z = Number(this._params.zoom);
    if (!Number.isFinite(z)) return;
    this._camera.position.z = z;
  }

  init(ctx) {
    if (this._inited) return;
    this._camera = typeof ctx?.getCamera === 'function' ? ctx.getCamera() : null;
    this._syncCameraZoom();
    this._buildMaterial();
    this._rebuildMesh();
    this._inited = true;
  }

  _buildMaterial() {
    if (this._material) this._material.dispose();
    this._material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uFrequency: { value: 5.5 },
        uSpeed: { value: 1.1 },
        uAmplitude: { value: 0.42 },
        uLightDirView: { value: new THREE.Vector3(0, 0.5, 0.86).normalize() },
        uAmbient: { value: 0.18 },
        uDiffuse: { value: 0.65 },
        uSpecular: { value: 0.55 },
        uIridescence: { value: 0.85 },
        uIridHueSpread: { value: 1.35 },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      side: THREE.DoubleSide,
    });
  }

  _rebuildMesh() {
    if (!this._material) return;
    if (this._mesh) {
      this._mesh.geometry.dispose();
      this.threeScene.remove(this._mesh);
    }
    const geo = new THREE.PlaneGeometry(5.5, 5.5, this._segmentsX, this._segmentsY);
    this._mesh = new THREE.Mesh(geo, this._material);
    this._mesh.rotation.x = -0.22;
    this.threeScene.add(this._mesh);
  }

  /**
   * @param {number} dt
   * @param {Record<string, number>} [resolved]
   */
  update(dt, resolved = {}) {
    const density = resolved.density !== undefined ? resolved.density : 0.45;
    const speed = resolved.speed !== undefined ? resolved.speed : 0.35;
    const light = resolved.light !== undefined ? resolved.light : 0.5;
    const irid = resolved.iridescence !== undefined ? resolved.iridescence : 0.75;

    const d = clamp01(density);
    const sp = clamp01(speed);
    const lt = clamp01(light);
    const ir = clamp01(irid);

    this._time += dt;

    const mat = this._material;
    if (!mat) return;

    mat.uniforms.uTime.value = this._time;
    mat.uniforms.uFrequency.value = lerp(2.2, 12.5, d);
    mat.uniforms.uSpeed.value = lerp(0.35, 2.2, sp);
    mat.uniforms.uAmplitude.value = lerp(0.28, 0.58, 0.35 + d * 0.5);

    mat.uniforms.uAmbient.value = lerp(0.06, 0.32, lt);
    mat.uniforms.uDiffuse.value = lerp(0.25, 1.05, lt);
    mat.uniforms.uSpecular.value = lerp(0.2, 0.95, lt);

    mat.uniforms.uIridescence.value = lerp(0.35, 1.0, ir);
    mat.uniforms.uIridHueSpread.value = lerp(0.85, 2.0, ir);

    if (this._camera) {
      this._lightView.copy(this._worldLightDir).transformDirection(this._camera.matrixWorldInverse);
      mat.uniforms.uLightDirView.value.copy(this._lightView);
    }
  }

  resize(_w, _h) {}

  dispose() {
    if (this._mesh) {
      this._mesh.geometry.dispose();
      this.threeScene.remove(this._mesh);
    }
    if (this._material) {
      this._material.dispose();
    }
    this._mesh = null;
    this._material = null;
    this._camera = null;
    this._inited = false;
  }
}
